import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { Eye, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import DateFilter from '../../components/DateFilter';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import Dropdown from '../../components/Dropdown';
// Reusing AddLead component for the view modal
import AddLead from '../leads/AddLead';
import { useAuth } from '../../context/AuthContext';

const CustomerList = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  
  // Pagination & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const { default: api } = await import('../../utils/api');
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        isCustomer: 'true'
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      
      const res = await api.get(`/leads?${queryParams.toString()}`);
      let dataToFormat = res.data.data || res.data;
      
      const convertedLeads = dataToFormat.map(lead => ({
        id: lead.id,
        name: lead.companyName,
        contact: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        contactPhone: lead.contactPhone,
        contactEmail: lead.contactEmail,
        frontCard: lead.frontCard,
        backCard: lead.backCard,
        address: lead.address,
        address2: lead.address2,
        city: lead.city,
        state: lead.state,
        pincode: lead.pincode,
        agencyType: lead.agencyType,
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        machineBrand: lead.machine?.name || '',
        machineModel: lead.machineModel || '',
        type: lead.leadType?.name ? lead.leadType.name : (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
        assignee: lead.user?.name || 'Unassigned',
        status: lead.status?.name || 'Completed',
        source: lead.leadSource?.name || 'Unknown',
        createdAt: lead.createdAt,
        createdBy: lead.createdBy || 'Unknown',
        notes: lead.notes,
        followups: lead.followups || [],
        conversionDate: lead.updatedAt
      }));
      setCustomers(convertedLeads);
      
      if (res.data.data) {
        setTotalPages(res.data.totalPages || 1);
        setTotalCustomers(res.data.total || 0);
      } else {
        setTotalPages(1);
        setTotalCustomers(res.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch customers', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleView = (customer) => {
    setViewingCustomer(customer);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setViewingCustomer(null);
  };

  // Filter Logic
  const roleFilteredCustomers = user?.role !== 'Admin' ? customers.filter(c => c.assignee === user?.name || c.createdBy === user?.name) : customers;

  const paginatedCustomers = roleFilteredCustomers.filter(customer => {
    const matchesDate = isDateInRange(customer.conversionDate, dateFilter);
    return matchesDate;
  });

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Customers</h2>
          <p className="text-muted mt-2">View details of leads that have successfully converted into customers.</p>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex justify-between items-center gap-4 flex-wrap">
             <div style={{ position: 'relative', width: '300px' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                <input 
                  type="text" 
                  placeholder="Search customers..." 
                  className="input-field" 
                  style={{ paddingLeft: '2.5rem' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
             </div>
             <div className="flex gap-4">
                <DateFilter value={dateFilter} onChange={setDateFilter} />
             </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <th style={{ padding: '1rem' }}>Conversion Date</th>
                <th style={{ padding: '1rem' }}>Company Name</th>
                <th style={{ padding: '1rem' }}>Contact Person</th>
                <th style={{ padding: '1rem' }}>Phone</th>
                <th style={{ padding: '1rem' }}>Source</th>
                <th style={{ padding: '1rem' }}>Assigned To</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCustomers.length > 0 ? paginatedCustomers.map(customer => (
                <tr key={customer.id} className="hover-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '1rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                    {formatAppDate(customer.conversionDate)}
                  </td>
                  <td style={{ padding: '1rem', fontWeight: '500', color: 'var(--color-text-main)' }}>{customer.name}</td>
                  <td style={{ padding: '1rem' }}>{customer.contact}</td>
                  <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>
                    <div>
                      <a href={`tel:${customer.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{customer.phone}</a>
                    </div>
                    <div className="text-xs mt-1">
                      <a href={`mailto:info@${customer.name.replace(/\s+/g, '').toLowerCase()}.com`} style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>info@{customer.name.replace(/\s+/g, '').toLowerCase()}.com</a>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{customer.source}</td>
                  <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{customer.assignee}</td>
                  <td style={{ padding: '1rem' }}>
                    <div className="flex justify-center gap-3">
                      <button className="text-primary hover:bg-blue-50 p-1.5 rounded transition-colors" title="View Details" onClick={() => handleView(customer)}>
                        <Eye size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No customers found matching your criteria.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalCustomers > 0 && (
          <div className="flex justify-between items-center mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, totalCustomers)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{totalCustomers}</span> customers
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button 
                  key={idx} 
                  className={`btn ${currentPage === idx + 1 ? 'btn-primary' : ''}`}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: currentPage === idx + 1 ? 'var(--color-primary)' : 'white',
                    color: currentPage === idx + 1 ? 'white' : 'var(--color-text-main)',
                    border: currentPage === idx + 1 ? 'none' : '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  onClick={() => handlePageChange(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && viewingCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem',
          animation: 'fadeIn 0.2s'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead 
              onClose={handleCloseModal} 
              initialData={viewingCustomer}
              isViewMode={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
